import { ref, type Ref } from 'vue';

import {
  clampAgentPanelWidth,
  readStoredAgentPanelWidth,
  storeAgentPanelWidth,
} from './agent-addon.config';

const panelOpen = ref(false);
const panelWidth = ref(readStoredAgentPanelWidth());
const isResizing = ref(false);

export interface AgentPanelController {
  panelOpen: Ref<boolean>;
  panelWidth: Ref<number>;
  isResizing: Ref<boolean>;
  togglePanel: () => void;
  closePanel: () => void;
  beginResize: (event: PointerEvent) => void;
  clampPanelWidthToViewport: () => void;
}

function clampToViewport(width: number): number {
  return clampAgentPanelWidth(width, window.innerWidth);
}

export function useAgentPanel(): AgentPanelController {
  function togglePanel(): void {
    panelOpen.value = !panelOpen.value;
  }

  function closePanel(): void {
    panelOpen.value = false;
  }

  function clampPanelWidthToViewport(): void {
    panelWidth.value = clampToViewport(panelWidth.value);
  }

  function beginResize(event: PointerEvent): void {
    if (event.button !== 0) return;

    isResizing.value = true;
    const startX = event.clientX;
    const startWidth = panelWidth.value;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    function onPointerMove(ev: PointerEvent): void {
      const delta = startX - ev.clientX;
      panelWidth.value = clampToViewport(startWidth + delta);
    }

    function endResize(): void {
      isResizing.value = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      storeAgentPanelWidth(panelWidth.value);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endResize);
      window.removeEventListener('pointercancel', endResize);
    }

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endResize);
    window.addEventListener('pointercancel', endResize);
    event.preventDefault();
  }

  return {
    panelOpen,
    panelWidth,
    isResizing,
    togglePanel,
    closePanel,
    beginResize,
    clampPanelWidthToViewport,
  };
}
